import React from 'react';
import PropTypes from 'prop-types';
import GridContainer from 'components/UI/Grid/GridContainer';
import GridItem from 'components/UI/Grid/GridItem';
import { withTranslation } from 'react-i18next';
import withStyles from '@material-ui/core/styles/withStyles';
// formik
import * as Yup from 'yup';
import { Formik } from 'formik';
// material-ui-core
import InputLabel from '@material-ui/core/InputLabel/index';
import Select from '@material-ui/core/Select/index';
import MenuItem from '@material-ui/core/MenuItem/index';
import FormControl from '@material-ui/core/FormControl/index';
import CustomInput from 'components/UI/CustomInput/CustomInput';
import Button from 'components/UI/CustomButtons/Button';

// routes
import { RADAR } from 'routes/paths';
import RouterContext from 'helpers/routerContext';

// styles
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';

import { defaultProcessorListFilters } from 'config';
import qs from 'helpers/queryStrings';

/**
 * @description
 * Validation form's rules.
 * @param {Function} t - Translation function from HOC.
 * @returns {Object} - YUP shape.
 */
const translatedValidationSchema = t =>
  Yup.object({
    value: Yup.string().required(t('errors.required')),
  });

class RadarCreate extends React.Component {
  static contextType = RouterContext;

  handleCancel = () => {
    const history = this.context;

    const { initialFilters, routePaths } = this.props;
    const urlString = qs.stringify({
      ...initialFilters,
    });

    history.push({ pathname: routePaths.LIST, search: urlString });
  };

  handleSubmitForm = data => {
    const {
      onSubmit,
      editMode,
      data: { id: originalId },
    } = this.props;

    const dataToSend = {
      listName: data.listName.toUpperCase(),
      group: data.group,
      value: data.value,
      description: data.description,
    };

    if (editMode) {
      dataToSend.id = originalId;
    }
    onSubmit(dataToSend);
  };

  render() {
    const {
      classes,
      t,
      listNames,
      listGroups,
      loadingSubmit,
      editMode,
      data: { listName, group, value, description },
      initialFilters: { listName: listNameUrl, group: groupUrl },
    } = this.props;

    const initialValues = {
      listName:
        (listName && listName.toLowerCase()) ||
        (listNameUrl && listNameUrl.toLowerCase()) ||
        defaultProcessorListFilters.listName,
      group: group || groupUrl || defaultProcessorListFilters.group,
      value: value || '',
      description: description || '',
    };
    const validationSchema = translatedValidationSchema(t);

    return (
      <div className={classes.pageContainer}>
        <h3 className={classes.title}>
          {editMode ? t('radar.edit.title') : t('radar.create.title')}
        </h3>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={this.handleSubmitForm}
          validateOnBlur={false}
        >
          {({ values, errors, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <div className={classes.contentContainer}>
                <GridContainer>
                  <GridItem xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      className={classes.selectFormControl}
                    >
                      <InputLabel
                        htmlFor="listName"
                        className={classes.selectLabel}
                      >
                        {t('radar.create.name')}
                      </InputLabel>
                      <Select
                        MenuProps={{
                          className: classes.selectMenu,
                        }}
                        classes={{
                          select: classes.select,
                        }}
                        value={values.listName}
                        onChange={event => {
                          handleChange(event);
                        }}
                        inputProps={{
                          name: 'listName',
                        }}
                      >
                        <MenuItem
                          disabled
                          classes={{
                            root: classes.selectMenuItem,
                          }}
                        >
                          {t('radar.list.filters.name')}
                        </MenuItem>
                        {listNames.map(listNameItem => (
                          <MenuItem
                            classes={{
                              root: classes.selectMenuItem,
                              selected: classes.selectMenuItemSelected,
                            }}
                            value={listNameItem.name}
                            key={listNameItem.name}
                          >
                            {listNameItem.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem xs={12} sm={6}>
                    <FormControl
                      fullWidth
                      className={classes.selectFormControl}
                    >
                      <InputLabel
                        htmlFor="group"
                        className={classes.selectLabel}
                      >
                        {t('radar.create.group')}
                      </InputLabel>
                      <Select
                        MenuProps={{
                          className: classes.selectMenu,
                        }}
                        classes={{
                          select: classes.select,
                        }}
                        value={values.group}
                        onChange={event => handleChange(event)}
                        inputProps={{
                          name: 'group',
                        }}
                      >
                        <MenuItem
                          disabled
                          classes={{
                            root: classes.selectMenuItem,
                          }}
                        >
                          {t('radar.create.group')}
                        </MenuItem>
                        {listGroups.map(listGroup => (
                          <MenuItem
                            classes={{
                              root: classes.selectMenuItem,
                              selected: classes.selectMenuItemSelected,
                            }}
                            value={listGroup}
                            key={listGroup}
                          >
                            {listGroup}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                </GridContainer>
                <GridContainer>
                  <GridItem xs={12} sm={6}>
                    <CustomInput
                      labelText={t('radar.create.value')}
                      formControlProps={{
                        fullWidth: true,
                      }}
                      helpText={errors.value}
                      error={Boolean(errors.value)}
                      inputProps={{
                        value: values.value,
                        name: 'value',
                        id: 'value',
                        onChange: e => handleChange(e),
                      }}
                    />
                  </GridItem>
                </GridContainer>
                <GridContainer>
                  <GridItem xs={12}>
                    <CustomInput
                      labelText={t('radar.create.descr')}
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        name: 'description',
                        value: values.description,
                        onChange: e => handleChange(e),
                      }}
                    />
                  </GridItem>
                </GridContainer>
                <GridContainer justify="space-between">
                  <GridItem>
                    <Button
                      id="cancel-btn"
                      color="danger"
                      simple
                      onClick={this.handleCancel}
                    >
                      {t('common.cancel')}
                    </Button>
                  </GridItem>
                  <GridItem>
                    <Button
                      disabled={loadingSubmit}
                      color="primary"
                      type="submit"
                      id="submit-button"
                    >
                      {editMode
                        ? t('radar.edit.submit')
                        : t('radar.create.submit')}
                    </Button>
                  </GridItem>
                </GridContainer>
              </div>
            </form>
          )}
        </Formik>
      </div>
    );
  }
}

/**
 * RadarListCreate PropTypes
 * @param listNames - prop processor list names query's results
 * @param listGroup - prop processor list group query's results
 * @param t - prop from HOC with string translations
 * @param loadingSubmit - loading when mutation is executing
 * @param editMode - bool to know when is edition form
 * @param data - item detail values
 * @callback onSubmit - function called when user submits
 */
RadarCreate.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  t: PropTypes.func.isRequired,
  listNames: PropTypes.instanceOf(Array).isRequired,
  listGroups: PropTypes.instanceOf(Array).isRequired,
  onSubmit: PropTypes.func.isRequired,
  loadingSubmit: PropTypes.bool,
  data: PropTypes.instanceOf(Object),
  editMode: PropTypes.bool,
  initialFilters: PropTypes.instanceOf(Object),
  routePaths: PropTypes.shape({
    LIST: PropTypes.string.isRequired,
  }),
};

RadarCreate.defaultProps = {
  data: {},
  loadingSubmit: false,
  editMode: false,
  initialFilters: {},
  routePaths: RADAR,
};

// to fix: The withStyles HOC set contextTypes to a children component
const RadarCreateWrapper = props => <RadarCreate {...props} />;

export default withTranslation()(
  withStyles({ ...extendedTablesStyle, ...extendedFormsStyle })(
    RadarCreateWrapper,
  ),
);
